import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import type { Stripe } from '@stripe/stripe-js';
import { debounce } from 'es-toolkit/compat';

import { defaultCurrency } from '~/constants/localizationDefaults';
import type { CookieValues } from '~/hooks/useUtils/useCookies';
import { PriceInterval, type PricingDetails } from '~/interfaces/subscriptions';

import getCheckoutAmountCents from '../utils/getCheckoutAmountCents';

interface Props {
  donationAmountCents: number;
  stripe: Stripe | null;
  pricingDetails: PricingDetails;
  priceId: string;
  premiumOfferId?: string;
  email: string;
  cookies: CookieValues;
  fetcher: any;
  setEmail: (email: string) => void;
  shouldCreatePaymentRequest?: boolean;
}

interface ReturnProps {
  paymentRequest: any;
  applePayEvent: any;
  setApplePayEvent: (event: any) => void;
  onPaymentSubmit: () => void;
}

const generateInitialPaymentRequest = (
  pricingDetails: PricingDetails,
  priceId: string,
  donationAmountCents: number,
) => {
  const tier = pricingDetails.tiers.find((t) => t.prices.some((p) => p.id === priceId));
  const price = tier?.prices.find((p) => p.id === priceId);

  return {
    currency: price?.currency || defaultCurrency,
    label: price?.cta || 'Pay now',
    amount: getCheckoutAmountCents(price, donationAmountCents),
  };
};

const useApplePay = ({
  donationAmountCents,
  stripe,
  pricingDetails,
  priceId,
  premiumOfferId,
  email,
  cookies,
  fetcher,
  setEmail,
  shouldCreatePaymentRequest = false,
}: Props): ReturnProps => {
  const [paymentRequest, setPaymentRequest] = useState<any>(null);
  const [applePayEvent, setApplePayEvent] = useState<any>(null);

  // Sets the initial Apple Pay payment request
  useEffect(() => {
    if (stripe && shouldCreatePaymentRequest) {
      const { currency, label, amount } = generateInitialPaymentRequest(
        pricingDetails,
        priceId,
        Number(donationAmountCents) || 0,
      );

      const pr = stripe.paymentRequest({
        country: 'US',
        currency,
        total: {
          label,
          amount,
        },
        requestPayerName: true,
        requestPayerEmail: true,
        disableWallets: ['googlePay', 'browserCard'],
      });

      pr.canMakePayment()
        .then((result: any) => {
          if (result) {
            setPaymentRequest(pr);
          }
        })
        .catch((err: any) => {
          console.log(err);
        });
    }
  }, [stripe, shouldCreatePaymentRequest]);

  const updatePaymentRequest = (newAmount: number, cta?: string) => {
    paymentRequest.update({
      total: {
        label: cta || 'Pay now',
        amount: newAmount,
      },
    });
  };

  const getPrice = (priceId: string) => {
    return pricingDetails.tiers.flatMap((t) => t.prices).find((p) => p.id === priceId);
  };

  const debouncedUpdatePaymentRequest = debounce(updatePaymentRequest, 100);

  // Handle event updates
  useEffect(() => {
    if (paymentRequest) {
      const price = getPrice(priceId);

      if (price?.interval === PriceInterval.DONATION) {
        try {
          updatePaymentRequest(Number(donationAmountCents) || 0, price?.cta);
        } catch (e) {
          console.log(e);
        }
      } else {
        updatePaymentRequest(getCheckoutAmountCents(price, Number(donationAmountCents) || 0), price?.cta);
      }
    }
  }, [priceId]);

  // Handle donation amount changes
  // Use debounced method to update payment request
  // as price can change frequently when the user is
  // typing in the price input
  useEffect(() => {
    if (paymentRequest) {
      const price = getPrice(priceId);

      if (price?.interval === PriceInterval.DONATION) {
        try {
          debouncedUpdatePaymentRequest(Number(donationAmountCents) || 0, price?.cta);
        } catch (e) {
          console.log(e);
        }
      }
    }
  }, [donationAmountCents]);

  const handlePaymentRequest = async (event: any) => {
    const paymentMethodId = event?.paymentMethod?.id || '';

    if (!paymentMethodId) {
      return;
    }

    setApplePayEvent(event);

    const formData = new FormData();
    formData.append('payment_method', paymentMethodId);
    formData.append('price_id', priceId);
    formData.append('premium_offer_id', premiumOfferId || '');
    formData.append('email', email);
    formData.append('last_resource_guid', cookies.last_resource_guid || '');
    if (cookies.visit_token) {
      formData.append('visit_token', cookies.visit_token);
    }
    formData.append('force_three_d_secure', 'false');
    formData.append('amount_cents', String(donationAmountCents));

    fetcher.submit(formData, { method: 'post', action: '/upgrade' });
    setEmail(email);
  };

  const onPaymentSubmit = () => {
    if (!email) {
      toast.error('Email is required');
      return;
    }

    paymentRequest.on('paymentmethod', handlePaymentRequest);
  };

  return {
    paymentRequest,
    applePayEvent,
    setApplePayEvent,
    onPaymentSubmit,
  };
};

export default useApplePay;
